import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { standardCommand } from '../../../core/model/command';
import { SelectionItem } from '../../../core/component/select/select.component';
import { IndexClauseSettings, IndexSeries } from '../../model/index-clause-settings';
import { IndexClauseSettingsService } from '../../service/data/index-clause-settings.service';

@Component({
  selector: 'app-index-clause-settings-form',
  templateUrl: './index-clause-settings-form.component.html',
  styleUrls: ['./index-clause-settings-form.component.scss'],
  providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: IndexClauseSettingsService }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IndexClauseSettingsFormComponent extends FormComponentBase<IndexClauseSettings> {
  private readonly _noPercentageChangeRounding = -1;

  percentageChangeRoundingSelection = [
    new SelectionItem<number>('INDEX_CLAUSE_SETTINGS.ROUNDING.NONE', this._noPercentageChangeRounding),
    new SelectionItem<number>('INDEX_CLAUSE_SETTINGS.ROUNDING.INTEGER', 0),
    new SelectionItem<number>('INDEX_CLAUSE_SETTINGS.ROUNDING.ONE_DECIMAL', 1),
    new SelectionItem<number>('INDEX_CLAUSE_SETTINGS.ROUNDING.TWO_DECIMALS', 2),
    new SelectionItem<number>('INDEX_CLAUSE_SETTINGS.ROUNDING.THREE_DECIMALS', 3),
    new SelectionItem<number>('INDEX_CLAUSE_SETTINGS.ROUNDING.FOUR_DECIMALS', 4),
  ];

  indexSeriesSelection = [
    new SelectionItem<IndexSeries>('INDEX_CLAUSE_SETTINGS.INDEX_SERIES.KPI1980', IndexSeries.KPI1980),
    new SelectionItem<IndexSeries>('INDEX_CLAUSE_SETTINGS.INDEX_SERIES.KPI2020', IndexSeries.KPI2020),
  ];

  fromBaseIndexSelection = [
    new SelectionItem<boolean>('INDEX_CLAUSE_SETTINGS.FROM_BASE_INDEX', true),
    new SelectionItem<boolean>('INDEX_CLAUSE_SETTINGS.FROM_CURRENT_INDEX', false),
  ];

  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    service: IndexClauseSettingsService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  protected createModel(): IndexClauseSettings {
    return new IndexClauseSettings();
  }

  protected prepareSave(): IndexClauseSettings {
    const model = super.prepareSave();
    if (model.percentageChangeRounding === this._noPercentageChangeRounding) {
      model.percentageChangeRounding = null;
    }

    return model;
  }

  protected getFormGroup(): FormGroup {
    return this._fb.group({
      caption: this._fc.createWithOptions('INDEX_CLAUSE_SETTINGS.CAPTION', this.dataContext.caption, { required: true, length: 100 }),
      fromBaseIndex: this._fc.create(
        'INDEX_CLAUSE_SETTINGS.CALCULATION_BASIS',
        this.dataContext.fromBaseIndex ?? true,
        null,
        null,
        'INDEX_CLAUSE_SETTINGS.HELP_TEXT.FROM_BASE_INDEX'
      ),
      allowLowerThanBaseIndex: this._fc.create(
        'INDEX_CLAUSE_SETTINGS.ALLOW_LOWER_THAN_BASE_INDEX',
        this.dataContext.allowLowerThanBaseIndex ?? false,
        null,
        null,
        'INDEX_CLAUSE_SETTINGS.HELP_TEXT.ALLOW_LOWER_THAN_BASE_INDEX'
      ),
      minPercentageChange: this._fc.create(
        'INDEX_CLAUSE_SETTINGS.MIN_PERCENTAGE_CHANGE',
        this.dataContext.minPercentageChange,
        null,
        null,
        'INDEX_CLAUSE_SETTINGS.HELP_TEXT.MIN_PERCENTAGE_CHANGE'
      ),
      maxPercentageChange: this._fc.create(
        'INDEX_CLAUSE_SETTINGS.MAX_PERCENTAGE_CHANGE',
        this.dataContext.maxPercentageChange,
        null,
        null,
        'INDEX_CLAUSE_SETTINGS.HELP_TEXT.MAX_PERCENTAGE_CHANGE'
      ),
      percentageChangeRounding: this._fc.create(
        'INDEX_CLAUSE_SETTINGS.PERCENTAGE_CHANGE_ROUNDING',
        this._getPercentageChangeRoundingFormValue(this.dataContext.percentageChangeRounding),
        null,
        null,
        'INDEX_CLAUSE_SETTINGS.HELP_TEXT.PERCENTAGE_CHANGE_ROUNDING'
      ),
      indexSeries: this._fc.createWithOptions('INDEX_CLAUSE_SETTINGS.INDEX_SERIES', this.dataContext.indexSeries, { required: true }),
    });
  }

  private _getPercentageChangeRoundingFormValue(value: number | null): number {
    return value === null || value === undefined ? this._noPercentageChangeRounding : value;
  }
}
